/**
 * aethereum prompt — push a one-shot prompt to one agent or the whole team.
 *
 * Unlike a directive (a standing order), a prompt is a single message: it
 * broadcasts to every agent (injecting live into running sessions) or targets
 * one teammate by handle (landing on their next turn).
 */

import { resolveHostedConfig, type HostedFlags } from "./hosted-config.js";
import { HostedWriteClient } from "../hive/hosted-client.js";

export interface PromptOptions extends HostedFlags {
  to?: string;
  env?: { AETHEREUM_SYNC_API_URL?: string; AETHEREUM_SYNC_TOKEN?: string };
  fetchImpl?: typeof fetch;
}

export async function runPrompt(text: string, options: PromptOptions = {}): Promise<void> {
  const cfg = resolveHostedConfig(options, options.env ?? process.env);
  if ("error" in cfg) {
    console.error(cfg.error);
    process.exit(1);
    return;
  }

  const trimmed = (text ?? "").trim();
  if (!trimmed) {
    console.error('Usage: aethereum prompt "<text>" [--to <handle>]');
    process.exit(1);
    return;
  }

  const client = new HostedWriteClient(cfg.baseUrl, cfg.token, options.fetchImpl ?? fetch);

  try {
    const resolved = await client.prompt(trimmed, options.to);

    if (!options.to) {
      console.log("✓ prompt broadcast to all agents (injects live into running sessions)");
      return;
    }

    if (resolved === null) {
      console.warn(`no teammate named ${options.to}; sent to no one`);
      return;
    }

    console.log(`✓ prompt sent to ${resolved} (lands on their next turn)`);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }
}
